import Avatar from 'components/Avatar'
import { FormGroup } from 'components/inputs'
import {
  CustomSelect,
  SingleItemSelectedDefault,
  useCustomSelectStateFromInfiniteScrollProvider,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import { observer } from 'mobx-react'
import * as React from 'react'
import LegendSelectorViewModel from '../../../../LegendSelectorViewModel'
import { isNotNil } from 'utils/object-util'
import { PublishedLegend } from 'legends/PublishedLegend'

interface CompactLegendSelectorProps {
  vm: LegendSelectorViewModel
  disabled?: boolean
  searchResults?: PublishedLegend[]
}

export default observer(function CompactLegendSelector({
  vm,
  disabled,
  searchResults,
}: CompactLegendSelectorProps) {
  const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.scrollProvider,
    searchResults: searchResults ?? vm.filteredLegends,
    searchText: vm.searchText,
    onSearchTextChange: vm.setSearchText,
  })

  // TODO: Once the job creation editor is fully migrated to the modal dialog, this
  // should be baked into the viewmodel. Keeping it separate right now to avoid messing
  // with the existing experience while allowing to override the disabled behavior,
  // e.g: preventing legend switching after job is created.
  const isDisabled = isNotNil(disabled) ? disabled : vm.disabled

  return (
    <FormGroup>
      <CustomSelect
        {...selectProps}
        placeholder="Select a legend"
        itemToString={(v) => String(v?.name)}
        focus$={vm.step.becomeActive$}
        onFocus={vm.step.onBecameActive}
        disabled={isDisabled}
        value={vm.selectedLegendVersion ?? undefined}
        onChange={vm.selectLegend}
        multi={false}
        data-test="compact-legend-selector"
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            visual={
              <Avatar
                size={24}
                subject={{
                  displayName: itemProps.item.name,
                  avatar: itemProps.item.imageUrl,
                }}
              />
            }
            {...itemProps}
          />
        )}
        renderSearchItem={({ item, itemToString, ...itemProps }) => (
          <SearchResultItem
            {...itemProps}
            visual={
              <Avatar
                size={24}
                subject={{
                  avatar: item.imageUrl,
                  displayName: item.name,
                }}
              />
            }
          >
            {item.name}
          </SearchResultItem>
        )}
      />
    </FormGroup>
  )
})
